/**
 * BAM Consulting catalog — tiers for org-level coaching customers.
 *
 * Distinct from `addons.js` because:
 *   - Subscription is at the org level, not the individual coach
 *   - Mirrors into `consulting_subscriptions` table (not `user_addons`)
 *   - Has 6-month commitment, not month-to-month
 *   - Orgs become "BAM Partner Org" after 6mo of continuous billing
 *
 * Phase 0 ships the data + webhook handlers. UI (org dashboard, checkout
 * flow, partner badge) ships in Phase 1.
 *
 * To wire a tier:
 *   1. Coleman creates the Stripe Product + Price in dashboard, sends back
 *      the price ID
 *   2. Replace the `price_TODO_*` placeholder below
 *   3. Mirror the price ID into supabase/functions/stripe-webhook/index.ts
 *      (CONSULTING_PRICE_TO_TIER map) AND into
 *      supabase/functions/create-consulting-checkout/index.ts (TIER_PRICE_IDS)
 */

export const CONSULTING_TIERS = {
  consulting_essentials: {
    key: "consulting_essentials",
 name: "BAM Consulting: Essentials",
    tagline: "Strategy + structure for growing programs.",
    description:
      "Monthly 1:1 strategy call with a BAM coach + access to our coaching " +
      "ops playbook + group call across the consulting cohort.",
    bullets: [
      "Monthly 1:1 strategy call (60 min)",
      "Group call with peer programs",
      "BAM coaching ops playbook",
      "Email support during business hours",
    ],
    commitment_months: 6,
    prices: {
      monthly: {
        stripe_price_id: "price_TODO_CONSULTING_ESSENTIALS_MONTHLY",
        amount_cents: 49900,
        interval: "month",
        display: "$499/mo",
      },
      yearly: {
        stripe_price_id: "price_TODO_CONSULTING_ESSENTIALS_YEARLY",
        amount_cents: 499000,
        interval: "year",
        display: "$4,990/yr",
        savings_label: "save ~17%",
      },
    },
  },

  consulting_plus: {
    key: "consulting_plus",
 name: "BAM Consulting: Plus",
 tagline: "Deep partnership: we're in your corner.",
    description:
      "Everything in Essentials plus weekly office hours, custom curriculum " +
      "review, and direct slack channel with BAM coaches.",
    bullets: [
      "Everything in Essentials",
      "Weekly office hours (group)",
      "Custom curriculum review",
      "Direct Slack channel with BAM coaches",
      "Quarterly business review",
    ],
    commitment_months: 6,
    prices: {
      monthly: {
        stripe_price_id: "price_TODO_CONSULTING_PLUS_MONTHLY",
        amount_cents: 99900,
        interval: "month",
        display: "$999/mo",
      },
      yearly: {
        stripe_price_id: "price_TODO_CONSULTING_PLUS_YEARLY",
        amount_cents: 999000,
        interval: "year",
        display: "$9,990/yr",
        savings_label: "save ~17%",
      },
    },
  },

  consulting_custom: {
    key: "consulting_custom",
 name: "BAM Consulting: Custom",
 tagline: "Built for you: let's talk.",
    description:
      "Embedded BAM coaching staff, retreats, custom builds, brand " +
      "collaboration. Priced per-engagement; invoice-billed.",
    bullets: [
      "Embedded BAM coaching staff",
      "Retreat hosting + travel",
      "Custom curriculum builds",
      "Brand collaboration opportunities",
      "Priced per-engagement, invoice-billed",
    ],
    commitment_months: 6,
    // No public prices — handled via sales conversation + invoice.
    prices: {},
    contact_only: true,
  },
};

export const CONSULTING_TIER_KEYS = Object.keys(CONSULTING_TIERS);

// Months of continuous active billing before an org earns the "BAM Partner
// Org" badge. Read by app-level maintenance + the partner-badge derivation.
export const PARTNER_ORG_THRESHOLD_MONTHS = 6;

// Beta-gate: until BAM Consulting is ready for public sale, only
// allowlisted admins see the Consulting surface. Same pattern as addons.js.
// Live (Coleman, 2026-10-13): the consulting teaser shows to everyone.
export const consultingLockedToAllowlist = false;
